import type { ChatRunStatus } from './hooks/use-chats';

const NOTIFICATION_ICON = '/favicon.png';

// In-app navigation for a clicked notification, registered by whoever has
// the router (useRunCompletionNotifications in the app sidebar). A page load
// would end every response still streaming in the tab, so this is never a
// location change; before anything registers, a click just focuses the tab.
let navigate: ((path: string) => void) | null = null;

export const setNotificationNavigator = (fn: ((path: string) => void) | null) => {
  navigate = fn;
};

/**
 * Fire a desktop notification when a chat's response finishes, but only if
 * the user is not looking at the tab. `document.hidden` is the source of
 * truth for tab visibility, so no extra state is needed; a visible tab has
 * the sidebar's spinner and unread dot instead.
 *
 * The chat id is the Notification `tag`, which controls dedupe: same tag
 * replaces the previous notification, different tags stack — so several turns
 * in one conversation collapse into one rolling notice, and the two paths that
 * can report the same finish (the live stream's onFinish and the sidebar's
 * run-status poll) never show it twice.
 */
export const maybeNotifyRunFinished = (
  chatId: string,
  status: Extract<ChatRunStatus, 'completed' | 'failed'> = 'completed',
  title?: string | null,
) => {
  if (typeof window === 'undefined') return;
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  if (typeof document !== 'undefined' && !document.hidden) return;
  try {
    const n = new Notification(
      status === 'failed' ? 'HiLo Agent response interrupted' : 'HiLo Agent response ready',
      {
        body: title
          ? `${title}: ${status === 'failed' ? 'the response was interrupted.' : 'your response is ready.'}`
          : status === 'failed'
            ? 'A chat response was interrupted.'
            : 'Your chat response is ready.',
        icon: NOTIFICATION_ICON,
        tag: `hilo-chat-${chatId}`,
      },
    );
    n.onclick = () => {
      window.focus();
      n.close();
      const path = `/chat/${chatId}`;
      if (window.location.pathname !== path) navigate?.(path);
    };
  } catch {
    /* noop */
  }
};
